import React, { useEffect, useState, useCallback } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      color: theme.palette.text.disabled,
      marginBottom: theme.spacing(1.5)
    },
    item: {
      padding: theme.spacing(0.5, 1),
      borderRadius: 4,
      cursor: "pointer",
      fontSize: 13,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      "&:hover": {
        backgroundColor: theme.palette.action.hover
      }
    },
    itemH2: {
      fontWeight: 600,
      color: theme.palette.text.primary
    },
    itemActive: {
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.primary.main,
      fontWeight: 600
    },
    itemH3: {
      paddingLeft: theme.spacing(3),
      fontSize: 12
    }
  })
);

interface TocEntry {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  content: string;
  scrollContainer?: HTMLElement | null;
}

const TableOfContents: React.FC<TableOfContentsProps> = ({ content, scrollContainer }) => {
  const classes = useStyles();
  const [activeId, setActiveId] = useState<string>("");
  const [entries, setEntries] = useState<TocEntry[]>([]);

  useEffect(() => {
    if (!scrollContainer) return undefined;

    const timeout = setTimeout(() => {
      // MarkdownRenderer renders h2 as <Typography variant="h5"> → <h5>, h3 as <h6>
      const headings = scrollContainer.querySelectorAll("h5[id], h6[id]");
      const tocEntries: TocEntry[] = Array.from(headings).map((h) => ({
        id: h.id,
        text: h.textContent || "",
        level: h.tagName.toLowerCase() === "h5" ? 2 : 3
      }));

      setEntries(tocEntries);
    }, 100);

    return () => clearTimeout(timeout);
  }, [scrollContainer, content]);

  const handleClick = useCallback((id: string) => {
    if (!scrollContainer) return;

    const element = scrollContainer.querySelector(`#${CSS.escape(id)}`);

    if (element) {
      const containerTop = scrollContainer.getBoundingClientRect().top;
      const elementTop = element.getBoundingClientRect().top;
      const offset = elementTop - containerTop + scrollContainer.scrollTop - 16;

      scrollContainer.scrollTo({ top: offset, behavior: "smooth" });
    }
  }, [scrollContainer]);

  useEffect(() => {
    if (!scrollContainer) return undefined;

    const observer = new IntersectionObserver(
      (observerEntries) => {
        for (const entry of observerEntries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      {
        root: scrollContainer,
        rootMargin: "-20% 0px -70% 0px"
      }
    );

    const headings = scrollContainer.querySelectorAll("h5[id], h6[id]");

    headings.forEach((heading) => observer.observe(heading));

    return () => observer.disconnect();
  }, [scrollContainer, content]);

  if (entries.length === 0) return null;

  return (
    <div>
      <Typography className={classes.title}>Índice</Typography>
      {entries.map((entry) => (
        <div
          key={entry.id}
          className={`${classes.item} ${entry.level === 2 ? classes.itemH2 : ""} ${entry.level === 3 ? classes.itemH3 : ""} ${
            activeId === entry.id ? classes.itemActive : ""
          }`}
          onClick={() => handleClick(entry.id)}
          title={entry.text}
        >
          {entry.text}
        </div>
      ))}
    </div>
  );
};

export default TableOfContents;
